'use client';

import { useState, useEffect, useCallback, Suspense, useRef } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  PACKAGE_PRICES,
  VIDEO_ADDON_PRICE,
  FIRST_EXP_BUNDLE_DISCOUNT,
  computeTotalCents,
  type PackageId as PricingPackageId,
} from '@/lib/pricing';

type PackageId = 'pkg_first_exp' | 'pkg_4h' | 'pkg_10h';
type LocationId = 'loc1' | 'loc2';
type BookingStep = 1 | 2 | 3 | 4;
type Step4Phase = 'payment' | 'confirmation' | 'two_sessions_pending';
type UsageMode4h = 'couple_same_day' | 'two_sessions_two_days' | '';

interface BookingState {
  location: LocationId | '';
  packageId: PackageId | '';
  usageMode4h: UsageMode4h;
  date: string;
  timeSlot: string;
  slotId: string;
  // Two-session fields (pkg_4h + two_sessions_two_days only)
  session1Date: string;
  session1TimeSlot: string;
  session1SlotId: string;
  session2Date: string;
  session2TimeSlot: string;
  session2SlotId: string;
  // Two-session package booking id (shared across both reservations)
  packageBookingId: string;
  guests: number;
  addVideoInsta360: boolean;
  isGift: boolean;
  notes: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferredLang: 'it' | 'en' | '';
  acceptTerms: boolean;
  acceptPrivacy: boolean;
  reminderWhatsapp: boolean;
  paymentType: 'full' | 'deposit';
  bookingRef: string;
  reservationId: string;
  waiverToken: string;
  reservationStatus: string;
}

interface TimeSlot {
  time: string;
  available: boolean;
  slotId?: string;
  startTime?: string;
}

const PACKAGES = {
  pkg_first_exp: { nameIT: 'Prima Esperienza', nameEN: 'First Experience', price: PACKAGE_PRICES.pkg_first_exp, durationH: 1, depositAllowed: false },
  pkg_4h: { nameIT: '4 Ore', nameEN: '4 Hours', price: PACKAGE_PRICES.pkg_4h, durationH: 4, depositAllowed: true },
  pkg_10h: { nameIT: '10 Ore', nameEN: '10 Hours', price: PACKAGE_PRICES.pkg_10h, durationH: 10, depositAllowed: true },
};

const LOCATIONS = {
  loc1: { nameIT: 'Alice Beach — Lago Maggiore', nameEN: 'Alice Beach — Lake Maggiore', apiKey: 'alice_beach' },
  loc2: { nameIT: 'Spiaggia Miami — Lago d\'Orta', nameEN: 'Spiaggia Miami — Lake Orta', apiKey: 'spiaggia_miami' },
};

const VIDEO_INSTA360_PRICE = VIDEO_ADDON_PRICE;

// Stripe promise — initialized once
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function StepIndicator({ step, lang }: { step: BookingStep; lang: 'it' | 'en' }) {
  const steps = lang === 'it'
    ? ['Selezione', 'Extra', 'Dati', 'Conferma']
    : ['Selection', 'Extras', 'Details', 'Confirm'];

  return (
    <div style={{ marginBottom: '32px' }}>
      <div style={{ height: '3px', background: 'rgba(240,247,255,0.08)', borderRadius: '100px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${((step - 1) / 3) * 100}%`,
          background: 'linear-gradient(90deg, #00D4FF, #33ddff)',
          borderRadius: '100px',
          transition: 'width 0.5s cubic-bezier(0.22,1,0.36,1)',
        }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {steps.map((label, i) => {
          const s = (i + 1) as BookingStep;
          const status = s < step ? 'completed' : s === step ? 'active' : 'inactive';
          return (
            <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1 }}>
              <div className={`step-dot ${status}`}>
                {status === 'completed' ? <Icon name="CheckIcon" size={14} /> : s}
              </div>
              <span style={{
                fontSize: '10px', fontWeight: 600, letterSpacing: '0.04em',
                color: status === 'active' ? '#00D4FF' : status === 'completed' ? '#25D366' : 'rgba(240,247,255,0.3)',
                textAlign: 'center',
              }}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MiniCalendar({ selectedDate, onSelect, firstBookableDate: minDate }: {
  selectedDate: string;
  onSelect: (date: string) => void;
  firstBookableDate?: string;
}) {
  const [viewYear, setViewYear] = useState<number | null>(null);
  const [viewMonth, setViewMonth] = useState<number | null>(null);
  const [todayStr, setTodayStr] = useState<string | null>(null);

  useEffect(() => {
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    setTodayStr(now.toDateString());
  }, []);

  if (viewYear === null || viewMonth === null || todayStr === null) {
    return (
      <div style={{ background: '#122940', border: '1px solid rgba(240,247,255,0.1)', borderRadius: '16px', padding: '16px', minHeight: '220px' }} />
    );
  }

  const today = new Date(todayStr);
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;

  const monthNames = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  const dayNames = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

  const goBack = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => (y ?? 0) - 1); }
    else setViewMonth(m => (m ?? 1) - 1);
  };
  const goFwd = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => (y ?? 0) + 1); }
    else setViewMonth(m => (m ?? 0) + 1);
  };

  return (
    <div style={{ background: '#122940', border: '1px solid rgba(240,247,255,0.1)', borderRadius: '16px', padding: '16px', userSelect: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'rgba(240,247,255,0.5)', cursor: 'pointer', padding: '4px' }}>
          <Icon name="ChevronLeftIcon" size={16} />
        </button>
        <span style={{ fontSize: '14px', fontWeight: 700 }}>{monthNames[viewMonth]} {viewYear}</span>
        <button onClick={goFwd} style={{ background: 'none', border: 'none', color: 'rgba(240,247,255,0.5)', cursor: 'pointer', padding: '4px' }}>
          <Icon name="ChevronRightIcon" size={16} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
        {dayNames.map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 700, color: 'rgba(240,247,255,0.3)', padding: '4px 0' }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {[...Array(adjustedFirstDay)].map((_, i) => <div key={`e${i}`} />)}
        {[...Array(daysInMonth)].map((_, i) => {
          const day = i + 1;
          const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isSelected = dateStr === selectedDate;
          // Use server-provided firstBookableDate when available; fall back to
          // browser-based "today" only as a last resort (avoids blank calendar
          // on first render before the API response arrives).
          const isDisabled = minDate ? dateStr < minDate : new Date(dateStr) < today;
          const isToday = day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();
          const hasSlots = !isDisabled;

          return (
            <button
              key={day}
              onClick={() => !isDisabled && onSelect(dateStr)}
              disabled={isDisabled}
              style={{
                aspectRatio: '1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                border: isSelected ? 'none' : isToday ? '1px solid rgba(0,212,255,0.4)' : '1px solid transparent',
                background: isSelected ? '#00D4FF' : 'transparent',
                color: isSelected ? '#0B1F2E' : isDisabled ? 'rgba(240,247,255,0.2)' : '#F0F7FF',
                fontWeight: isSelected ? 700 : 500,
                position: 'relative',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              {day}
              {hasSlots && !isSelected && (
                <span style={{
                  position: 'absolute', bottom: '2px', left: '50%', transform: 'translateX(-50%)',
                  width: '4px', height: '4px', borderRadius: '50%', background: '#00D4FF',
                }} />
              )}
            </button>
          );
        })}
      </div>
      <p style={{ fontSize: '11px', color: 'rgba(240,247,255,0.35)', marginTop: '10px', textAlign: 'center' }}>• = slot disponibili</p>
    </div>
  );
}

// Inline spinner component
function Spinner({ size = 16 }: { size?: number }) {
  return (
    <div style={{
      width: `${size}px`, height: `${size}px`,
      border: `2px solid rgba(240,247,255,0.15)`,
      borderTopColor: '#00D4FF',
      borderRadius: '50%',
      animation: 'spin-slow 0.8s linear infinite',
      flexShrink: 0,
    }} />
  );
}

// ─── STRIPE PAYMENT FORM ─────────────────────────────────────────────────────
interface StripePaymentFormProps {
  clientSecret: string;
  amountCents: number;
  totalAmountCents: number;
  paymentType: 'full' | 'deposit';
  lang: 'it' | 'en';
  onSuccess: (paymentIntentId: string) => void;
  onError: (msg: string) => void;
}

function StripePaymentFormInner({
  amountCents,
  totalAmountCents,
  paymentType,
  lang,
  onSuccess,
  onError,
}: Omit<StripePaymentFormProps, 'clientSecret'>) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripeError, setStripeError] = useState('');

  useEffect(() => {
    const patchAllIframes = () => {
      const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe');
      iframes.forEach((iframe) => {
        const current = iframe.getAttribute('allow') || '';
        if (!current.includes('payment')) {
          iframe.setAttribute(
            'allow',
            current ? `${current}; payment; fullscreen` : 'payment; fullscreen'
          );
        }
      });
    };

    patchAllIframes();
    const observer = new MutationObserver(patchAllIframes);
    observer.observe(document.body, { childList: true, subtree: true });
    const t1 = setTimeout(patchAllIframes, 500);
    const t2 = setTimeout(patchAllIframes, 1500);
    const t3 = setTimeout(patchAllIframes, 3000);

    return () => {
      observer.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setStripeError('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/booking`,
      },
      redirect: 'if_required',
    });

    if (error) {
      const msg = error.message ?? (lang === 'it' ? 'Pagamento non riuscito. Riprova.' : 'Payment failed. Please try again.');
      setStripeError(msg);
      onError(msg);
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      const msg = lang === 'it' ? 'Pagamento non completato. Riprova.' : 'Payment not completed. Please try again.';
      setStripeError(msg);
      onError(msg);
      setIsProcessing(false);
    }
  };

  const depositLabel = lang === 'it' ? 'Acconto 30%' : '30% Deposit';
  const fullLabel = lang === 'it' ? 'Totale' : 'Total';
  const chargeLabel = paymentType === 'deposit' ? depositLabel : fullLabel;
  const chargeAmount = (amountCents / 100).toFixed(2);
  const totalAmount = (totalAmountCents / 100).toFixed(2);

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{
        background: 'rgba(0,212,255,0.06)',
        border: '1px solid rgba(0,212,255,0.2)',
        borderRadius: '12px',
        padding: '14px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span style={{ fontSize: '13px', color: 'rgba(240,247,255,0.7)' }}>{chargeLabel}</span>
        <span style={{ fontSize: '20px', fontWeight: 700, color: '#00D4FF' }}>€{chargeAmount}</span>
      </div>
      {paymentType === 'deposit' && (
        <p style={{ fontSize: '12px', color: 'rgba(240,247,255,0.45)', textAlign: 'center', marginTop: '-12px' }}>
          {lang === 'it'
            ? `Saldo rimanente €${(totalAmountCents / 100 - amountCents / 100).toFixed(2)} da pagare il giorno della sessione`
            : `Remaining balance €${(totalAmountCents / 100 - amountCents / 100).toFixed(2)} due on session day`}
        </p>
      )}

      <div style={{
        background: '#0d2235',
        border: '1px solid rgba(240,247,255,0.1)',
        borderRadius: '12px',
        padding: '16px',
      }}>
        <PaymentElement
          options={{
            layout: { type: 'tabs', defaultCollapsed: false },
            wallets: { applePay: 'auto', googlePay: 'auto' },
          }}
        />
      </div>

      {stripeError && (
        <div style={{
          background: 'rgba(255,80,80,0.1)',
          border: '1px solid rgba(255,80,80,0.3)',
          borderRadius: '10px',
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          color: '#ff7070',
        }}>
          <Icon name="ExclamationCircleIcon" size={16} />
          {stripeError}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || !elements || isProcessing}
        className="btn-primary"
        style={{
          width: '100%',
          justifyContent: 'center',
          fontSize: '15px',
          padding: '16px',
          opacity: (!stripe || !elements || isProcessing) ? 0.7 : 1,
        }}
      >
        {isProcessing ? (
          <>
            <Spinner size={16} />
            {lang === 'it' ? 'Elaborazione...' : 'Processing...'}
          </>
        ) : (
          <>
            <Icon name="LockClosedIcon" size={16} variant="solid" />
            {lang === 'it' ? `Paga €${chargeAmount}` : `Pay €${chargeAmount}`}
          </>
        )}
      </button>

      <p style={{ fontSize: '11px', color: 'rgba(240,247,255,0.3)', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
        <Icon name="LockClosedIcon" size={11} />
        {lang === 'it' ? 'Pagamento sicuro via Stripe · SSL 256-bit' : 'Secure payment via Stripe · 256-bit SSL'}
      </p>
    </form>
  );
}

// ─── BOOKING ENGINE INNER ────────────────────────────────────────────────────
function BookingEngineInner({ lang = 'it' }: { lang?: 'it' | 'en' }) {
  const searchParams = useSearchParams();
  const router = useRouter();

  const [step, setStep] = useState<BookingStep>(1);
  const [step4Phase, setStep4Phase] = useState<Step4Phase>('payment');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preselected, setPreselected] = useState<{ pkg?: string; location?: string } | null>(null);
  const [bookingError, setBookingError] = useState('');

  // Guard: prevents double-reserve for two_sessions_two_days
  const twoSessionReserveCompletedRef = useRef(false);

  // Step 1 availability state (for 1h and couple_same_day flows)
  const [availabilitySlots, setAvailabilitySlots] = useState<TimeSlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState('');

  // Server-provided firstBookableDate — used by MiniCalendar to disable blocked days.
  // Populated from the availability API response so we never rely on the browser clock.
  const [serverFirstBookableDate, setServerFirstBookableDate] = useState<string | undefined>(undefined);

  // Two-session availability state
  const [session1Slots, setSession1Slots] = useState<TimeSlot[]>([]);
  const [isLoadingSession1, setIsLoadingSession1] = useState(false);
  const [session1Error, setSession1Error] = useState('');

  const [session2Slots, setSession2Slots] = useState<TimeSlot[]>([]);
  const [isLoadingSession2, setIsLoadingSession2] = useState(false);
  const [session2Error, setSession2Error] = useState('');

  // Step 4 waiver state
  const [isLoadingWaiver, setIsLoadingWaiver] = useState(false);
  const [waiverError, setWaiverError] = useState('');

  // Step 4 payment state
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [paymentAmountCents, setPaymentAmountCents] = useState(0);
  const [paymentTotalCents, setPaymentTotalCents] = useState(0);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);

  // Discount code state (Step 4)
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscountCode, setAppliedDiscountCode] = useState('');
  const [discountAmountCents, setDiscountAmountCents] = useState(0);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState('');

  const [state, setState] = useState<BookingState>({
    location: '',
    packageId: '',
    usageMode4h: '',
    date: '',
    timeSlot: '',
    slotId: '',
    session1Date: '',
    session1TimeSlot: '',
    session1SlotId: '',
    session2Date: '',
    session2TimeSlot: '',
    session2SlotId: '',
    packageBookingId: '',
    guests: 1,
    addVideoInsta360: false,
    isGift: false,
    notes: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    preferredLang: '',
    acceptTerms: false,
    acceptPrivacy: false,
    reminderWhatsapp: false,
    paymentType: 'full',
    bookingRef: '',
    reservationId: '',
    waiverToken: '',
    reservationStatus: '',
  });

  useEffect(() => {
    const pkg = searchParams.get('pkg') as PackageId | null;
    const location = searchParams.get('location') as LocationId | null;
    if (pkg || location) {
      setPreselected({ pkg: pkg || undefined, location: location || undefined });
      setState(prev => ({
        ...prev,
        packageId: (pkg && PACKAGES[pkg as PackageId]) ? pkg : prev.packageId,
        location: (location && LOCATIONS[location as LocationId]) ? location : prev.location,
      }));
    }
  }, [searchParams]);

  // Fetch firstBookableDate from the server on mount so MiniCalendar is
  // correctly disabled before the user selects a date (avoids relying on
  // the browser clock for the cutoff rule).
  useEffect(() => {
    fetch('/api/booking/availability?location=alice_beach&from=' + encodeURIComponent(new Date().toISOString()) + '&to=' + encodeURIComponent(new Date(Date.now() + 60000).toISOString()) + '&partySize=1&packageId=pkg_first_exp')
      .then(r => r.json())
      .then(d => { if (d.firstBookableDate) setServerFirstBookableDate(d.firstBookableDate); })
      .catch(() => {/* non-fatal — calendar falls back to browser today */});
  }, []);

  const generateLocalSlots = (): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    for (let hour = 9; hour <= 18; hour++) {
      for (let min = 0; min < 60; min += 30) {
        if (hour === 18 && min > 30) break;
        const hh = String(hour).padStart(2, '0');
        const mm = String(min).padStart(2, '0');
        slots.push({ time: `${hh}:${mm}`, available: true, slotId: undefined });
      }
    }
    return slots;
  };

  // Returns the UTC boundaries for a calendar date interpreted in Europe/Rome (DST-safe)
  function romeDayBounds(dateStr: string): { from: string; to: string } {
    const [y, m, d] = dateStr.split('-').map(Number);
    // Use noon UTC to detect the Rome offset on that day (avoids DST edge at midnight)
    const noonUtc = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    const romeHourStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Rome',
      hour: '2-digit',
      hour12: false,
    }).format(noonUtc);
    const offsetHours = parseInt(romeHourStr, 10) - 12; // +1 (CET) or +2 (CEST)
    const fromMs = Date.UTC(y, m - 1, d, 0, 0, 0) - offsetHours * 3_600_000;
    const toMs   = Date.UTC(y, m - 1, d, 23, 59, 59) - offsetHours * 3_600_000;
    return { from: new Date(fromMs).toISOString(), to: new Date(toMs).toISOString() };
  }

  // Generic availability fetcher — supports optional usageMode for two_sessions_two_days
  const fetchAvailability = useCallback(async (
    date: string,
    location: LocationId | '',
    guests: number,
    packageId?: string,
    usageMode?: string,
  ) => {
    if (!date || !location) return;
    setIsLoadingSlots(true);
    setSlotsError('');
    setAvailabilitySlots([]);
    try {
      const locationKey = LOCATIONS[location as LocationId]?.apiKey ?? 'main';
      const { from, to } = romeDayBounds(date);
      const pkgId = packageId || 'pkg_first_exp';
      let url = `/api/booking/availability?location=${locationKey}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&partySize=${guests}&packageId=${pkgId}`;
      if (usageMode) url += `&usageMode=${encodeURIComponent(usageMode)}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok) {
        setAvailabilitySlots(generateLocalSlots());
        return;
      }
      // Capture server-provided firstBookableDate so MiniCalendar uses server time
      if (data.firstBookableDate) {
        setServerFirstBookableDate(data.firstBookableDate);
      }
      if (data.slots && data.slots.length > 0) {
        const dbSlotMap = new Map<string, { slotId: string; available: boolean; startTime: string }>();
        data.slots.forEach((s: any) => {
          const isoTime = s.startTime as string;
          const timeKey = new Intl.DateTimeFormat('it-IT', {
            timeZone: 'Europe/Rome',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }).format(new Date(isoTime));
          if (timeKey) {
            dbSlotMap.set(timeKey, {
              slotId: s.slotId,
              available: (s.availableCapacity ?? 0) > 0,
              startTime: s.startTime,
            });
          }
        });
        const fullGrid = generateLocalSlots();
        const mapped: TimeSlot[] = fullGrid.map(localSlot => {
          const dbSlot = dbSlotMap.get(localSlot.time);
          if (dbSlot) {
            return { time: localSlot.time, available: dbSlot.available, slotId: dbSlot.slotId, startTime: dbSlot.startTime };
          }
          return { ...localSlot, available: false, slotId: undefined };
        });
        setAvailabilitySlots(mapped);
      } else {
        setAvailabilitySlots(generateLocalSlots().map(s => ({ ...s, available: false })));
      }
    } catch {
      setAvailabilitySlots(generateLocalSlots());
    } finally {
      setIsLoadingSlots(false);
    }
  }, [lang]);

  // Dedicated fetcher for session 1 (two_sessions_two_days)
  const fetchSession1Availability = useCallback(async (date: string, location: LocationId | '') => {
    if (!date || !location) return;
    setIsLoadingSession1(true);
    setSession1Error('');
    setSession1Slots([]);
    try {
      const locationKey = LOCATIONS[location as LocationId]?.apiKey ?? 'main';
      const { from, to } = romeDayBounds(date);
      let url = `/api/booking/availability?location=${locationKey}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&partySize=2&packageId=pkg_4h&usageMode=two_sessions_two_days`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok) { setSession1Slots(generateLocalSlots()); return; }
      if (data.slots && data.slots.length > 0) {
        const dbSlotMap = new Map<string, { slotId: string; available: boolean; startTime: string }>();
        data.slots.forEach((s: any) => {
          const isoTime = s.startTime as string;
          const timeKey = new Intl.DateTimeFormat('it-IT', {
            timeZone: 'Europe/Rome',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }).format(new Date(isoTime));
          if (timeKey) dbSlotMap.set(timeKey, { slotId: s.slotId, available: (s.availableCapacity ?? 0) > 0, startTime: s.startTime });
        });
        const mapped: TimeSlot[] = generateLocalSlots().map(localSlot => {
          const dbSlot = dbSlotMap.get(localSlot.time);
          if (dbSlot) return { time: localSlot.time, available: dbSlot.available, slotId: dbSlot.slotId, startTime: dbSlot.startTime };
          return { ...localSlot, available: false, slotId: undefined };
        });
        setSession1Slots(mapped);
      } else {
        setSession1Slots(generateLocalSlots().map(s => ({ ...s, available: false })));
      }
    } catch {
      setSession1Slots(generateLocalSlots());
    } finally {
      setIsLoadingSession1(false);
    }
  }, []);

  // Dedicated fetcher for session 2 (two_sessions_two_days)
  const fetchSession2Availability = useCallback(async (date: string, location: LocationId | '') => {
    if (!date || !location) return;
    setIsLoadingSession2(true);
    setSession2Error('');
    setSession2Slots([]);
    try {
      const locationKey = LOCATIONS[location as LocationId]?.apiKey ?? 'main';
      const { from, to } = romeDayBounds(date);
      let url = `/api/booking/availability?location=${locationKey}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&partySize=2&packageId=pkg_4h&usageMode=two_sessions_two_days`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok) { setSession2Slots(generateLocalSlots()); return; }
      if (data.slots && data.slots.length > 0) {
        const dbSlotMap = new Map<string, { slotId: string; available: boolean; startTime: string }>();
        data.slots.forEach((s: any) => {
          const isoTime = s.startTime as string;
          const timeKey = new Intl.DateTimeFormat('it-IT', {
            timeZone: 'Europe/Rome',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }).format(new Date(isoTime));
          if (timeKey) dbSlotMap.set(timeKey, { slotId: s.slotId, available: (s.availableCapacity ?? 0) > 0, startTime: s.startTime });
        });
        const mapped: TimeSlot[] = generateLocalSlots().map(localSlot => {
          const dbSlot = dbSlotMap.get(localSlot.time);
          if (dbSlot) return { time: localSlot.time, available: dbSlot.available, slotId: dbSlot.slotId, startTime: dbSlot.startTime };
          return { ...localSlot, available: false, slotId: undefined };
        });
        setSession2Slots(mapped);
      } else {
        setSession2Slots(generateLocalSlots().map(s => ({ ...s, available: false })));
      }
    } catch {
      setSession2Slots(generateLocalSlots());
    } finally {
      setIsLoadingSession2(false);
    }
  }, []);

  // Auto-fetch availability for standard flow (1h, couple_same_day)
  useEffect(() => {
    if (state.packageId === 'pkg_4h' && state.usageMode4h === 'two_sessions_two_days') return;
    if (state.date && state.location) {
      fetchAvailability(state.date, state.location as LocationId, state.guests, state.packageId || 'pkg_first_exp');
      setState(prev => ({ ...prev, timeSlot: '', slotId: '' }));
    }
  }, [state.date, state.location, state.guests, state.packageId, state.usageMode4h, fetchAvailability]);

  // Auto-fetch session 1 availability when session1Date changes
  useEffect(() => {
    if (state.packageId === 'pkg_4h' && state.usageMode4h === 'two_sessions_two_days' && state.session1Date && state.location) {
      fetchSession1Availability(state.session1Date, state.location as LocationId);
      setState(prev => ({ ...prev, session1TimeSlot: '', session1SlotId: '' }));
    }
  }, [state.session1Date, state.location, state.packageId, state.usageMode4h, fetchSession1Availability]);

  // Auto-fetch session 2 availability when session2Date changes
  useEffect(() => {
    if (state.packageId === 'pkg_4h' && state.usageMode4h === 'two_sessions_two_days' && state.session2Date && state.location) {
      fetchSession2Availability(state.session2Date, state.location as LocationId);
      setState(prev => ({ ...prev, session2TimeSlot: '', session2SlotId: '' }));
    }
  }, [state.session2Date, state.location, state.packageId, state.usageMode4h, fetchSession2Availability]);

  const pkg = state.packageId ? PACKAGES[state.packageId as PackageId] : null;
  const firstExpDiscount = state.packageId === 'pkg_first_exp' && state.guests === 2 ? FIRST_EXP_BUNDLE_DISCOUNT : 0;
  const basePrice = pkg ? (state.packageId === 'pkg_4h' ? pkg.price : pkg.price * state.guests - firstExpDiscount) : 0;
  const videoPrice = state.addVideoInsta360 ? VIDEO_INSTA360_PRICE : 0;
  const totalAmount = state.packageId
    ? computeTotalCents({
        packageId: state.packageId as PricingPackageId,
        partySize: state.packageId === 'pkg_4h' ? 2 : state.guests,
        addVideo: state.addVideoInsta360,
      }) / 100
    : 0;

  const update = (field: keyof BookingState, value: unknown) => {
    setState(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateStep = (s: BookingStep): boolean => {
    const newErrors: Record<string, string> = {};
    const t = lang === 'it';

    if (s === 1) {
      if (!state.location) newErrors.location = t ? 'Seleziona una location' : 'Select a location';
      if (!state.packageId) newErrors.packageId = t ? 'Seleziona un pacchetto' : 'Select a package';

      // pkg_10h: block Continue — contact only
      if (state.packageId === 'pkg_10h') {
        newErrors.packageId = t
          ? 'Il pacchetto 10 ore richiede una pianificazione personalizzata su più sessioni. Contattaci su WhatsApp: ti aiutiamo a scegliere date, location e modalità più adatte.'
          : 'The 10-hour package requires custom planning across multiple sessions. Contact us on WhatsApp: we\'ll help you choose the best dates, location and setup.';
        setErrors(newErrors);
        return false;
      }

      if (state.packageId === 'pkg_4h' && !state.usageMode4h) {
        newErrors.usageMode4h = t ? 'Seleziona la modalità di utilizzo' : 'Select a usage mode';
      }

      // Two-session validation
      if (state.packageId === 'pkg_4h' && state.usageMode4h === 'two_sessions_two_days') {
        if (!state.session1Date) newErrors.session1Date = t ? 'Seleziona la data della Sessione 1' : 'Select date for Session 1';
        if (!state.session1SlotId) newErrors.session1TimeSlot = t ? 'Seleziona un orario per la Sessione 1' : 'Select a time slot for Session 1';
        if (!state.session2Date) newErrors.session2Date = t ? 'Seleziona la data della Sessione 2' : 'Select date for Session 2';
        if (!state.session2SlotId) newErrors.session2TimeSlot = t ? 'Seleziona un orario per la Sessione 2' : 'Select a time slot for Session 2';
        if (state.session1Date && state.session2Date && state.session1Date === state.session2Date) {
          newErrors.session2Date = t ? 'Le due sessioni devono essere in giorni diversi' : 'The two sessions must be on different days';
        }
      } else {
        // Standard flow validation
        if (!state.date) newErrors.date = t ? 'Seleziona una data' : 'Select a date';
        if (!state.timeSlot) newErrors.timeSlot = t ? 'Seleziona un orario' : 'Select a time slot';
        if (state.packageId !== 'pkg_4h') {
          if (!state.guests || state.guests < 1 || state.guests > 2) newErrors.guests = t ? 'Seleziona 1 o 2 partecipanti (sessione privata)' : 'Select 1 or 2 participants (private session)';
        }
      }
    }
    if (s === 3) {
      if (!state.firstName.trim()) newErrors.firstName = t ? 'Campo obbligatorio' : 'Required field';
      if (!state.lastName.trim()) newErrors.lastName = t ? 'Campo obbligatorio' : 'Required field';
      if (!state.email.trim()) {
        newErrors.email = t ? 'Email obbligatoria' : 'Email is required';
      } else if (!state.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        newErrors.email = t ? 'Email non valida' : 'Invalid email address';
      }
      if (!state.phone.trim()) {
        newErrors.phone = t ? 'Numero obbligatorio' : 'Phone number is required';
      } else if (state.phone.replace(/\D/g, '').length < 8) {
        newErrors.phone = t ? 'Numero troppo corto (min. 8 cifre)' : 'Number too short (min. 8 digits)';
      }
      if (!state.preferredLang) newErrors.preferredLang = t ? 'Seleziona una lingua' : 'Select a language';
      if (!state.acceptTerms) newErrors.acceptTerms = t ? 'Devi accettare i termini di servizio' : 'You must accept the Terms of Service';
      if (!state.acceptPrivacy) newErrors.acceptPrivacy = t ? 'Devi accettare la privacy policy' : 'You must accept the Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 3 → 4: Call /api/booking/reserve then fetch waiver token
  const handleReserve = async (): Promise<string | null> => {
    setIsSubmitting(true);
    setBookingError('');
    try {
      const pkg = state.packageId ? PACKAGES[state.packageId as PackageId] : null;
      const loc = state.location ? LOCATIONS[state.location as LocationId] : null;
      // NOTE: total is computed server-side via computeTotalCents in /api/booking/reserve.
      // We pass addVideo so the server can compute the authoritative total.
      // The local total below is only used as a legacy field in the request body (ignored server-side).
      const localTotalCents = state.packageId
        ? computeTotalCents({
            packageId: state.packageId as PricingPackageId,
            partySize: state.packageId === 'pkg_4h' ? 2 : state.guests,
            addVideo: state.addVideoInsta360,
          })
        : 0;

      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

      // ── TWO-SESSION PATH ──────────────────────────────────────────────────
      if (state.packageId === 'pkg_4h' && state.usageMode4h === 'two_sessions_two_days') {
        const s1SlotId = state.session1SlotId;
        const s2SlotId = state.session2SlotId;

        if (!s1SlotId || !uuidRegex.test(s1SlotId) || !s2SlotId || !uuidRegex.test(s2SlotId)) {
          setBookingError(lang === 'it' ? 'Slot non trovato. Ricarica la pagina e riprova.' : 'Slot not found. Please reload the page and try again.');
          return null;
        }

        const res = await fetch('/api/booking/reserve', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slotId: s1SlotId,
            secondSlotId: s2SlotId,
            customerName: `${state.firstName} ${state.lastName}`.trim(),
            customerEmail: state.email,
            customerPhone: state.phone || undefined,
            partySize: 2,
            specialRequests: state.notes || undefined,
            holdMinutes: 15,
            packageId: 'pkg_4h',
            usageMode: 'two_sessions_two_days',
            packageName: lang === 'it' ? '4 Ore — Due Sessioni' : '4 Hours — Two Sessions',
            locationName: loc ? (lang === 'it' ? loc.nameIT : loc.nameEN) : '',
            locationKey: loc?.apiKey ?? null,
            date: state.session1Date,
            time: state.session1TimeSlot,
            preferredLang: state.preferredLang || 'it',
            addVideo: state.addVideoInsta360,
            totalAmount: localTotalCents / 100,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          const msg = res.status === 409
            ? (lang === 'it' ? 'Uno degli slot non è più disponibile. Scegli altri orari.' : 'One of the slots is no longer available. Please choose different times.')
            : (data.error ?? (lang === 'it' ? 'Errore nella prenotazione. Riprova.' : 'Booking error. Please try again.'));
          setBookingError(msg);
          return null;
        }
        const session1 = data.sessions?.[0]?.reservation;
        if (!session1?.id) {
          setBookingError(lang === 'it' ? 'Errore nella prenotazione. Riprova.' : 'Booking error. Please try again.');
          return null;
        }
        let reservationId = session1.id as string;
        twoSessionReserveCompletedRef.current = true;
        setState(prev => ({
          ...prev,
          reservationId,
          reservationStatus: session1.reservationStatus,
          bookingRef: `EFX-${reservationId.slice(0, 8).toUpperCase()}`,
          packageBookingId: data.packageBookingId ?? '',
        }));
        return reservationId;
      }

      // ── STANDARD PATH (1h, couple_same_day) ──────────────────────────────
      let resolvedSlotId = state.slotId;
      if (!resolvedSlotId || !uuidRegex.test(resolvedSlotId)) {
        try {
          const locationKey = LOCATIONS[state.location as LocationId]?.apiKey ?? 'main';
          const { from, to } = romeDayBounds(state.date);
          const avRes = await fetch(
            `/api/booking/availability?location=${locationKey}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&partySize=${state.guests}&packageId=${state.packageId || 'pkg_first_exp'}`
          );
          if (avRes.ok) {
            const avData = await avRes.json();
            if (avData.slots && avData.slots.length > 0) {
              const matchingSlot = avData.slots.find((s: any) => {
                const isoTime = s.startTime as string;
                const timeKey = new Intl.DateTimeFormat('it-IT', {
                  timeZone: 'Europe/Rome',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                }).format(new Date(isoTime));
                return timeKey === state.timeSlot;
              });
              if (matchingSlot) resolvedSlotId = matchingSlot.slotId;
            }
          }
        } catch {
          // Non-fatal
        }
      }

      if (!resolvedSlotId || !uuidRegex.test(resolvedSlotId)) {
        setBookingError(lang === 'it' ? 'Slot non trovato. Ricarica la pagina e riprova.' : 'Slot not found. Please reload the page and try again.');
        return null;
      }

      const res = await fetch('/api/booking/reserve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slotId: resolvedSlotId,
          customerName: `${state.firstName} ${state.lastName}`.trim(),
          customerEmail: state.email,
          customerPhone: state.phone || undefined,
          partySize: state.packageId === 'pkg_4h' ? 2 : state.guests,
          specialRequests: state.notes || undefined,
          holdMinutes: 15,
          packageId: state.packageId || 'pkg_first_exp',
          usageMode: state.packageId === 'pkg_4h' ? state.usageMode4h : undefined,
          packageName: pkg ? (lang === 'it' ? pkg.nameIT : pkg.nameEN) : '',
          locationName: loc ? (lang === 'it' ? loc.nameIT : loc.nameEN) : '',
          locationKey: loc?.apiKey ?? null,
          date: state.date,
          time: state.timeSlot,
          preferredLang: state.preferredLang || 'it',
          addVideo: state.addVideoInsta360,
          totalAmount: localTotalCents / 100,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = res.status === 409
          ? (lang === 'it' ? 'Slot non più disponibile. Scegli un altro orario.' : 'Slot no longer available. Please choose another time.')
          : (data.error ?? (lang === 'it' ? 'Errore nella prenotazione. Riprova.' : 'Booking error. Please try again.'));
        setBookingError(msg);
        if (res.status === 409 && state.date && state.location) {
          fetchAvailability(state.date, state.location as LocationId, state.guests, state.packageId || 'pkg_first_exp');
        }
        return null;
      }
      let reservationId = data.reservation.id as string;
      const serverWaiverToken: string | null = data.waiverToken ?? null;
      setState(prev => ({
        ...prev,
        reservationId,
        reservationStatus: data.reservation.reservationStatus,
        bookingRef: `EFX-${reservationId.slice(0, 8).toUpperCase()}`,
        ...(serverWaiverToken ? { waiverToken: serverWaiverToken } : {}),
      }));
      return reservationId;
    } catch {
      setBookingError(lang === 'it' ? 'Errore di rete. Controlla la connessione e riprova.' : 'Network error. Check your connection and try again.');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create payment intent after reservation
  const createPaymentIntent = async (reservationId: string, paymentType: 'full' | 'deposit', forceNew = false) => {
    setIsCreatingIntent(true);
    setPaymentError('');
    try {
      const totalCents = Math.round(totalAmount * 100);
      const res = await fetch('/api/booking/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reservationId,
          amountCents: totalCents,
          paymentType,
          packageId: state.packageId,
          customerName: `${state.firstName} ${state.lastName}`.trim(),
          customerEmail: state.email,
          currency: 'eur',
          forceNew,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const statusLabel = `[HTTP ${res.status}]`;
        const errorMsg = data.error ?? (lang === 'it' ? 'Errore nella creazione del pagamento.' : 'Error creating payment.');
        const stripeType = data.stripeErrorType ? ` (${data.stripeErrorType})` : '';
        setPaymentError(`${statusLabel} ${errorMsg}${stripeType}`);
        return;
      }
      if (data.alreadyPaid) {
        setState(prev => ({ ...prev, reservationStatus: 'PAID_PENDING_SIGNATURE' }));
        setStep4Phase('confirmation');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      setClientSecret(data.clientSecret);
      setPaymentIntentId(data.paymentIntentId);
      setPaymentAmountCents(data.amountCents);
      setPaymentTotalCents(data.totalAmountCents);
    } catch (err: unknown) {
      setPaymentError(lang === 'it' ? 'Errore di rete. Riprova.' : 'Network error. Please try again.');
    } finally {
      setIsCreatingIntent(false);
    }
  };

  const handlePaymentSuccess = async (piId: string) => {
    if (step4Phase === 'confirmation') return;
    setIsConfirmingPayment(true);
    setPaymentError('');
    try {
      const res = await fetch('/api/booking/confirm-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reservationId: state.reservationId,
          stripePaymentIntentId: piId,
          amountPaidCents: paymentAmountCents,
          totalAmountCents: paymentTotalCents,
          paymentType: state.paymentType === 'deposit' ? 'DEPOSIT' : 'FULL',
          currency: 'eur',
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPaymentError(data.error ?? (lang === 'it' ? 'Errore nella conferma del pagamento.' : 'Error confirming payment.'));
        return;
      }
      setState(prev => ({ ...prev, reservationStatus: 'PAID_PENDING_SIGNATURE' }));
      setStep4Phase('confirmation');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setPaymentError(lang === 'it' ? 'Errore di rete durante la conferma.' : 'Network error during confirmation.');
    } finally {
      setIsConfirmingPayment(false);
    }
  };

  const fetchWaiverToken = useCallback(async (reservationId: string, firstName: string, lastName: string, email: string) => {
    if (!reservationId) return;
    setIsLoadingWaiver(true);
    setWaiverError('');
    try {
      const res = await fetch('/api/booking/waiver-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reservationId,
          signerName: `${firstName} ${lastName}`.trim(),
          signerEmail: email,
          waiverVersion: '1.0',
          tokenTtlHours: 48,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setWaiverError(data.error ?? (lang === 'it' ? 'Errore nella generazione del link waiver.' : 'Error generating waiver link.'));
        return;
      }
      setState(prev => ({ ...prev, waiverToken: data.waiver.signatureToken }));
    } catch {
      setWaiverError(lang === 'it' ? 'Errore di rete. Il link waiver non è disponibile.' : 'Network error. Waiver link unavailable.');
    } finally {
      setIsLoadingWaiver(false);
    }
  }, [lang]);

  const handleNext = async () => {
    if (!validateStep(step)) return;

    if (step === 3) {
      let reservationId = state.reservationId;
      const isTwoSession = state.packageId === 'pkg_4h' && state.usageMode4h === 'two_sessions_two_days';

      if (!reservationId && !(isTwoSession && twoSessionReserveCompletedRef.current)) {
        reservationId = await handleReserve() ?? '';
        if (!reservationId) return;
      } else if (isTwoSession && twoSessionReserveCompletedRef.current) {
        reservationId = state.reservationId;
      }

      // Two-session path: proceed to payment (same as standard path)
      // packageBookingId is stored in state and will be included in Stripe metadata via create-payment-intent
      setStep(4);
      setStep4Phase('payment');
      window.scrollTo({ top: 0, behavior: 'smooth' });

      await createPaymentIntent(reservationId, state.paymentType);

      setState(prev => {
        if (!prev.waiverToken) {
          setTimeout(() => {
            fetchWaiverToken(reservationId, prev.firstName, prev.lastName, prev.email);
          }, 0);
        }
        return prev;
      });
      return;
    }

    const nextStep = Math.min(step + 1, 4) as BookingStep;
    setStep(nextStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1) as BookingStep);
    setBookingError('');
    setClientSecret('');
    setPaymentIntentId('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePaymentTypeChange = async (newType: 'full' | 'deposit') => {
    if (newType === state.paymentType) return;
    update('paymentType', newType);
    if (step === 4 && step4Phase === 'payment' && state.reservationId) {
      setClientSecret('');
      setPaymentIntentId('');
      let reservationId = state.reservationId;
      await new Promise<void>(resolve => setTimeout(resolve, 0));
      await createPaymentIntent(reservationId, newType, true);
    }
  };

  // Apply discount code (Step 4)
  const handleApplyDiscount = async () => {
    if (!discountCode.trim() || !state.reservationId) return;
    setIsApplyingDiscount(true);
    setDiscountError('');
    try {
      const res = await fetch('/api/booking/apply-discount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reservationId: state.reservationId, code: discountCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDiscountError(data.error ?? (lang === 'it' ? 'Codice non valido' : 'Invalid code'));
        return;
      }
      // Success: store applied code and discount amount, then recreate payment intent
      setAppliedDiscountCode(discountCode.trim().toLowerCase());
      setDiscountAmountCents(data.discountAmountCents);
      setDiscountError('');
      // Recreate payment intent with new discounted total
      if (state.reservationId) {
        setClientSecret('');
        setPaymentIntentId('');
        await createPaymentIntent(state.reservationId, state.paymentType, true);
      }
    } catch {
      setDiscountError(lang === 'it' ? 'Errore di rete. Riprova.' : 'Network error. Please try again.');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // Remove applied discount code
  const handleRemoveDiscount = async () => {
    if (!state.reservationId) return;
    setIsApplyingDiscount(true);
    setDiscountError('');
    try {
      await fetch('/api/booking/apply-discount', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reservationId: state.reservationId }),
      });
      setAppliedDiscountCode('');
      setDiscountAmountCents(0);
      setDiscountCode('');
      // Recreate payment intent without discount
      if (state.reservationId) {
        setClientSecret('');
        setPaymentIntentId('');
        await createPaymentIntent(state.reservationId, state.paymentType, true);
      }
    } catch {
      // Non-fatal
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // ─── SLOT GRID RENDERER (reusable) ───────────────────────────────────────
  const renderSlotGrid = (
    slots: TimeSlot[],
    selectedTime: string,
    isLoading: boolean,
    error: string,
    onSelect: (time: string, slotId: string) => void,
    onRetry: () => void,
    locationSelected: boolean,
  ) => {
    if (isLoading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', color: 'rgba(240,247,255,0.5)', fontSize: '13px' }}>
          <Spinner size={16} />
          {lang === 'it' ? 'Caricamento disponibilità...' : 'Loading availability...'}
        </div>
      );
    }
    if (error) {
      return (
        <div className="alert-error" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', textAlign: 'left' }}>
          <Icon name="ExclamationCircleIcon" size={16} />
          {error}
          <button
            onClick={onRetry}
            style={{ marginLeft: 'auto', background: 'none', border: '1px solid rgba(0,212,255,0.4)', borderRadius: '8px', color: '#00D4FF', cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: '8px 16px' }}
          >
            {lang === 'it' ? 'Riprova' : 'Retry'}
          </button>
        </div>
      );
    }
    if (slots.length > 0) {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '8px' }}>
          {slots.map((slot) => (
            <button
              key={slot.time}
              onClick={() => { if (slot.available) onSelect(slot.time, slot.slotId ?? ''); }}
              disabled={!slot.available}
              className={`time-slot ${selectedTime === slot.time ? 'selected' : ''} ${!slot.available ? 'unavailable' : ''}`}
              style={{ border: 'none', cursor: slot.available ? 'pointer' : 'not-allowed', fontFamily: 'DM Sans, sans-serif', background: 'transparent' }}
            >
              {slot.time}
            </button>
          ))}
        </div>
      );
    }
    if (!locationSelected) {
      return (
        <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.4)', padding: '8px 0' }}>
          {lang === 'it' ? 'Seleziona una location per vedere gli slot.' : 'Select a location to see slots.'}
        </p>
      );
    }
    return null;
  };

  // ─── STEP 1: SELECTION ───────────────────────────────────────────────────
  const renderStep1 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {preselected && (
        <div className="alert-info" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icon name="CheckCircleIcon" size={16} variant="solid" />
          <span style={{ flex: 1, fontSize: '13px' }}>
            {lang === 'it' ? '✓ Selezione confermata' : '✓ Selection confirmed'}
            {preselected.pkg && ` · ${PACKAGES[preselected.pkg as PackageId]?.nameIT}`}
            {preselected.location && ` · ${LOCATIONS[preselected.location as LocationId]?.nameIT}`}
          </span>
          <button
            onClick={() => { setPreselected(null); router.replace('/booking'); }}
            style={{ background: 'none', border: 'none', color: '#00D4FF', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
          >
            {lang === 'it' ? 'Cambia' : 'Change'}
          </button>
        </div>
      )}

      {/* Location */}
      <div>
        <label className="form-label">{lang === 'it' ? 'Location *' : 'Location *'}</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {(Object.entries(LOCATIONS) as [LocationId, typeof LOCATIONS.loc1][]).map(([id, loc]) => (
            <div
              key={id}
              className={`radio-card ${state.location === id ? 'selected' : ''}`}
              onClick={() => update('location', id)}
              role="button" tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && update('location', id)}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
                  border: `2px solid ${state.location === id ? '#00D4FF' : 'rgba(240,247,255,0.2)'}`,
                  background: state.location === id ? '#00D4FF' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {state.location === id && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#0B1F2E' }} />}
                </div>
                <p style={{ fontSize: '13px', fontWeight: 700, lineHeight: 1.3 }}>
                  {lang === 'it' ? loc.nameIT : loc.nameEN}
                </p>
              </div>
            </div>
          ))}
        </div>
        {errors.location && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '6px' }}>{errors.location}</p>}
      </div>

      {/* Package */}
      <div>
        <label className="form-label">{lang === 'it' ? 'Pacchetto *' : 'Package *'}</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {(Object.entries(PACKAGES) as [PackageId, typeof PACKAGES.pkg_first_exp][]).map(([id, p]) => (
            <div
              key={id}
              className={`radio-card ${state.packageId === id ? 'selected' : ''}`}
              onClick={() => {
                update('packageId', id);
                if (id !== 'pkg_4h') update('usageMode4h', '');
              }}
              role="button" tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  update('packageId', id);
                  if (id !== 'pkg_4h') update('usageMode4h', '');
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${state.packageId === id ? '#00D4FF' : 'rgba(240,247,255,0.2)'}`,
                  background: state.packageId === id ? '#00D4FF' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {state.packageId === id && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#0B1F2E' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '14px', fontWeight: 700 }}>{lang === 'it' ? p.nameIT : p.nameEN}</p>
                  <p style={{ fontSize: '12px', color: 'rgba(240,247,255,0.5)' }}>
                    {p.durationH === 0.5 ? '30 min' : `${p.durationH}h`}{p.depositAllowed ? (lang === 'it' ? ' · Deposito 30% disponibile' : ' · 30% deposit available') : ''}
                  </p>
                </div>
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#00D4FF' }}>€{p.price}</span>
              </div>
            </div>
          ))}
        </div>
        {errors.packageId && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '6px' }}>{errors.packageId}</p>}
      </div>

      {/* pkg_10h — WhatsApp contact banner (blocks online booking) */}
      {state.packageId === 'pkg_10h' && (
        <div style={{
          background: 'rgba(37,211,102,0.07)',
          border: '1px solid rgba(37,211,102,0.3)',
          borderRadius: '14px',
          padding: '20px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <Icon name="ChatBubbleLeftRightIcon" size={22} style={{ color: '#25D366', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '14px', fontWeight: 700, color: '#F0F7FF', marginBottom: '6px' }}>
                {lang === 'it' ? 'Prenotazione su misura' : 'Custom booking required'}
              </p>
              <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.7)', lineHeight: 1.6 }}>
                {lang === 'it' ?'Il pacchetto 10 ore richiede una pianificazione personalizzata su più sessioni. Contattaci su WhatsApp: ti aiutiamo a scegliere date, location e modalità più adatte.' :'The 10-hour package requires custom planning across multiple sessions. Contact us on WhatsApp: we\'ll help you choose the best dates, location and setup.'}
              </p>
            </div>
          </div>
          <a
            href="https://wa.me/393757934676"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              background: '#25D366', color: '#fff', fontWeight: 700, fontSize: '14px',
              borderRadius: '10px', padding: '12px 20px', textDecoration: 'none',
              alignSelf: 'flex-start',
            }}
          >
            <Icon name="ChatBubbleLeftRightIcon" size={16} />
            {lang === 'it' ? 'Scrivici su WhatsApp' : 'Contact us on WhatsApp'}
          </a>
        </div>
      )}

      {/* 4h Usage Mode Selector */}
      {state.packageId === 'pkg_4h' && (
        <div>
          <label className="form-label">
            {lang === 'it' ? 'Come vuoi usare il pacchetto? *' : 'How do you want to use the package? *'}
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* Option 1: Couple same day */}
            <div
              className={`radio-card ${state.usageMode4h === 'couple_same_day' ? 'selected' : ''}`}
              onClick={() => { update('usageMode4h', 'couple_same_day'); update('guests', 2); }}
              role="button" tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && (update('usageMode4h', 'couple_same_day'), update('guests', 2))}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
                  border: `2px solid ${state.usageMode4h === 'couple_same_day' ? '#00D4FF' : 'rgba(240,247,255,0.2)'}`,
                  background: state.usageMode4h === 'couple_same_day' ? '#00D4FF' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {state.usageMode4h === 'couple_same_day' && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#0B1F2E' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '14px', fontWeight: 700 }}>
                    {lang === 'it' ? 'Coppia — stesso giorno' : 'Couple — same day'}
                  </p>
                  <p style={{ fontSize: '12px', color: 'rgba(240,247,255,0.5)', marginTop: '2px' }}>
                    {lang === 'it' ? '2 persone · 1 sessione da 2 ore' : '2 people · one 2-hour session'}
                  </p>
                </div>
              </div>
            </div>

            {/* Option 2: Two sessions two days */}
            <div
              className={`radio-card ${state.usageMode4h === 'two_sessions_two_days' ? 'selected' : ''}`}
              onClick={() => { update('usageMode4h', 'two_sessions_two_days'); update('guests', 2); }}
              role="button" tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && (update('usageMode4h', 'two_sessions_two_days'), update('guests', 2))}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
                  border: `2px solid ${state.usageMode4h === 'two_sessions_two_days' ? '#00D4FF' : 'rgba(240,247,255,0.2)'}`,
                  background: state.usageMode4h === 'two_sessions_two_days' ? '#00D4FF' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {state.usageMode4h === 'two_sessions_two_days' && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#0B1F2E' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '14px', fontWeight: 700 }}>
                    {lang === 'it' ? 'Due sessioni — due giorni' : 'Two sessions — two days'}
                  </p>
                  <p style={{ fontSize: '12px', color: 'rgba(240,247,255,0.5)', marginTop: '2px' }}>
                    {lang === 'it' ? '2 persone · 2 sessioni da 1 ora in giorni diversi' : '2 people · two 1-hour sessions on different days'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {errors.usageMode4h && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '6px' }}>{errors.usageMode4h}</p>}

          {/* ── TWO-SESSION SELECTOR (replaces WhatsApp-only block) ── */}
          {state.usageMode4h === 'two_sessions_two_days' && (
            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Participants badge */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)',
                borderRadius: '10px', padding: '10px 14px', fontSize: '13px', color: 'rgba(240,247,255,0.7)',
              }}>
                <Icon name="UserGroupIcon" size={16} style={{ color: '#00D4FF', flexShrink: 0 }} />
                {lang === 'it' ? 'Pacchetto due giorni · 2 partecipanti · €380 totale' : 'Two-day package · 2 participants · €380 total'}
              </div>

              {/* Session 1 */}
              <div style={{ background: 'rgba(240,247,255,0.03)', border: '1px solid rgba(240,247,255,0.08)', borderRadius: '14px', padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%', background: '#00D4FF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#0B1F2E' }}>1</span>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: 700 }}>
                    {lang === 'it' ? 'Sessione 1 — Primo giorno' : 'Session 1 — First day'}
                  </span>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                    {lang === 'it' ? 'Data *' : 'Date *'}
                  </label>
                  <MiniCalendar
                    selectedDate={state.session1Date}
                    onSelect={(d) => {
                      update('session1Date', d);
                      update('session1TimeSlot', '');
                      update('session1SlotId', '');
                    }}
                    firstBookableDate={serverFirstBookableDate}
                  />
                  {errors.session1Date && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '6px' }}>{errors.session1Date}</p>}
                </div>

                {state.session1Date && (
                  <div>
                    <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                      {lang === 'it' ? 'Orario *' : 'Time slot *'}
                    </label>
                    {renderSlotGrid(
                      session1Slots,
                      state.session1TimeSlot,
                      isLoadingSession1,
                      session1Error,
                      (time, slotId) => { update('session1TimeSlot', time); update('session1SlotId', slotId); twoSessionReserveCompletedRef.current = false; },
                      () => fetchSession1Availability(state.session1Date, state.location as LocationId),
                      !!state.location,
                    )}
                    {errors.session1TimeSlot && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '6px' }}>{errors.session1TimeSlot}</p>}
                    <p style={{ fontSize: '11px', color: 'rgba(240,247,255,0.35)', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon name="InformationCircleIcon" size={12} />
                      {lang === 'it' ? 'Slot da 1 ora · Max 2 rider' : '1-hour slots · Max 2 riders'}
                    </p>
                  </div>
                )}
              </div>

              {/* Session 2 */}
              <div style={{ background: 'rgba(240,247,255,0.03)', border: '1px solid rgba(240,247,255,0.08)', borderRadius: '14px', padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%', background: 'rgba(0,212,255,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#F0F7FF' }}>2</span>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: 700 }}>
                    {lang === 'it' ? 'Sessione 2 — Secondo giorno' : 'Session 2 — Second day'}
                  </span>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                    {lang === 'it' ? 'Data *' : 'Date *'}
                  </label>
                  <MiniCalendar
                    selectedDate={state.session2Date}
                    onSelect={(d) => {
                      update('session2Date', d);
                      update('session2TimeSlot', '');
                      update('session2SlotId', '');
                    }}
                    firstBookableDate={serverFirstBookableDate}
                  />
                  {errors.session2Date && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '6px' }}>{errors.session2Date}</p>}
                </div>

                {state.session2Date && (
                  <div>
                    <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                      {lang === 'it' ? 'Orario *' : 'Time slot *'}
                    </label>
                    {renderSlotGrid(
                      session2Slots,
                      state.session2TimeSlot,
                      isLoadingSession2,
                      session2Error,
                      (time, slotId) => { update('session2TimeSlot', time); update('session2SlotId', slotId); twoSessionReserveCompletedRef.current = false; },
                      () => fetchSession2Availability(state.session2Date, state.location as LocationId),
                      !!state.location,
                    )}
                    {errors.session2TimeSlot && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '6px' }}>{errors.session2TimeSlot}</p>}
                    <p style={{ fontSize: '11px', color: 'rgba(240,247,255,0.35)', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon name="InformationCircleIcon" size={12} />
                      {lang === 'it' ? 'Slot da 1 ora · Max 2 rider' : '1-hour slots · Max 2 riders'}
                    </p>
                  </div>
                )}
              </div>

              {/* Same-date validation error */}
              {state.session1Date && state.session2Date && state.session1Date === state.session2Date && (
                <div className="alert-error" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {lang === 'it' ? 'Le due sessioni devono essere in giorni diversi.' : 'The two sessions must be on different days.'}
                </div>
              )}

              {/* WhatsApp help CTA (secondary, not blocking) */}
              <div style={{
                background: 'rgba(37,211,102,0.06)',
                border: '1px solid rgba(37,211,102,0.2)',
                borderRadius: '12px',
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <Icon name="ChatBubbleLeftRightIcon" size={18} style={{ color: '#25D366', flexShrink: 0 }} />
                <p style={{ fontSize: '12px', color: 'rgba(240,247,255,0.6)', lineHeight: 1.5, flex: 1 }}>
                  {lang === 'it' ?'Hai dubbi su disponibilità o meteo? Scrivici su WhatsApp prima di procedere.' :'Questions about availability or weather? Message us on WhatsApp before continuing.'}
                </p>
                <a
                  href="https://wa.me/393757934676"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: '#25D366', color: '#fff', fontWeight: 700, fontSize: '12px',
                    borderRadius: '8px', padding: '8px 14px', textDecoration: 'none', flexShrink: 0,
                  }}
                >
                  <Icon name="ChatBubbleLeftRightIcon" size={14} />
                  WhatsApp
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Guests — hidden for pkg_4h (forced to 2) and pkg_10h (contact only) */}
      {state.packageId !== 'pkg_4h' && state.packageId !== 'pkg_10h' && (
        <div>
          <label className="form-label">{lang === 'it' ? 'Numero partecipanti' : 'Number of participants'}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => update('guests', Math.max(1, state.guests - 1))}
              style={{ width: '40px', height: '40px', borderRadius: '10px', border: '1px solid rgba(240,247,255,0.12)', background: '#1A3A52', color: '#F0F7FF', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'DM Sans, sans-serif' }}
            >−</button>
            <span style={{ fontSize: '18px', fontWeight: 700, minWidth: '32px', textAlign: 'center' }}>{state.guests}</span>
            <button
              onClick={() => update('guests', Math.min(2, state.guests + 1))}
              style={{ width: '40px', height: '40px', borderRadius: '10px', border: '1px solid rgba(240,247,255,0.12)', background: '#1A3A52', color: '#F0F7FF', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'DM Sans, sans-serif' }}
            >+</button>
            <span style={{ fontSize: '13px', color: 'rgba(240,247,255,0.45)' }}>
              {lang === 'it' ? 'max 2 per sessione privata' : 'max 2 per private session'}
            </span>
          </div>
          {errors.guests && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '6px' }}>{errors.guests}</p>}
          {/* 2-person bundle discount badge — visible only for pkg_first_exp with 2 guests */}
          {state.packageId === 'pkg_first_exp' && state.guests === 2 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              marginTop: '10px',
              background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.35)',
              borderRadius: '10px', padding: '8px 14px',
              fontSize: '13px', color: '#4ade80', fontWeight: 600,
            }}>
              <Icon name="TagIcon" size={15} style={{ color: '#4ade80', flexShrink: 0 }} />
              {lang === 'it' ?'Sconto 2 persone: €240 → €220 (−€20)' :'2-person bundle: €240 → €220 (−€20)'}
            </div>
          )}
        </div>
      )}

      {/* pkg_4h couple badge — shown when couple_same_day is selected */}
      {state.packageId === 'pkg_4h' && state.usageMode4h === 'couple_same_day' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)',
          borderRadius: '10px', padding: '10px 14px', fontSize: '13px', color: 'rgba(240,247,255,0.7)',
        }}>
          <Icon name="UserGroupIcon" size={16} style={{ color: '#00D4FF', flexShrink: 0 }} />
          {lang === 'it' ? 'Pacchetto coppia · 2 partecipanti inclusi' : 'Couple package · 2 participants included'}
        </div>
      )}

      {/* Date — hidden for two_sessions_two_days and pkg_10h */}
      {!(state.packageId === 'pkg_4h' && state.usageMode4h === 'two_sessions_two_days') && state.packageId !== 'pkg_10h' && (
        <div>
          <label className="form-label">{lang === 'it' ? 'Data *' : 'Date *'}</label>
          <MiniCalendar selectedDate={state.date} onSelect={(d) => {
            update('date', d);
            update('timeSlot', '');
            update('slotId', '');
          }} firstBookableDate={serverFirstBookableDate} />
          {errors.date && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '6px' }}>{errors.date}</p>}
        </div>
      )}

      {/* Time slots — hidden for two_sessions_two_days and pkg_10h */}
      {state.date && !(state.packageId === 'pkg_4h' && state.usageMode4h === 'two_sessions_two_days') && state.packageId !== 'pkg_10h' && (
        <div>
          <label className="form-label">{lang === 'it' ? 'Orario *' : 'Time slot *'}</label>

          {isLoadingSlots ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', color: 'rgba(240,247,255,0.5)', fontSize: '13px' }}>
              <Spinner size={16} />
              {lang === 'it' ? 'Caricamento disponibilità...' : 'Loading availability...'}
            </div>
          ) : slotsError ? (
            <div className="alert-error" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', textAlign: 'left' }}>
              <Icon name="ExclamationCircleIcon" size={16} />
              {slotsError}
              {state.location && (
                <button
                  onClick={() => fetchAvailability(state.date, state.location as LocationId, state.guests, state.packageId || 'pkg_first_exp')}
                  style={{ marginLeft: 'auto', background: 'none', border: '1px solid rgba(0,212,255,0.4)', borderRadius: '8px', color: '#00D4FF', cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: '8px 16px' }}
                >
                  {lang === 'it' ? 'Riprova' : 'Retry'}
                </button>
              )}
            </div>
          ) : availabilitySlots.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '8px' }}>
              {availabilitySlots.map((slot) => (
                <button
                  key={slot.time}
                  onClick={() => {
                    if (slot.available) {
                      update('timeSlot', slot.time);
                      update('slotId', slot.slotId ?? '');
                    }
                  }}
                  disabled={!slot.available}
                  className={`time-slot ${state.timeSlot === slot.time ? 'selected' : ''} ${!slot.available ? 'unavailable' : ''}`}
                  style={{ border: 'none', cursor: slot.available ? 'pointer' : 'not-allowed', fontFamily: 'DM Sans, sans-serif', background: 'transparent' }}
                >
                  {slot.time}
                </button>
              ))}
            </div>
          ) : (
            !state.location ? (
              <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.4)', padding: '8px 0' }}>
                {lang === 'it' ? 'Seleziona una location per vedere gli slot.' : 'Select a location to see slots.'}
              </p>
            ) : null
          )}

          {errors.timeSlot && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '6px' }}>{errors.timeSlot}</p>}
          <p style={{ fontSize: '11px', color: 'rgba(240,247,255,0.35)', marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
            <Icon name="InformationCircleIcon" size={12} />
            {lang === 'it' ? 'Orari 09:00–19:00 · Ogni slot 30 min · Max 2 rider per slot' : '09:00–19:00 · 30-min slots · Max 2 riders per slot'}
          </p>
        </div>
      )}
    </div>
  );

  // ─── STEP 2: EXTRAS ──────────────────────────────────────────────────────
  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <h3 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '22px', fontWeight: 700 }}>
        {lang === 'it' ? 'Extra & Note' : 'Extras & Notes'}
      </h3>

      <div
        onClick={() => update('addVideoInsta360', !state.addVideoInsta360)}
        style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          background: state.addVideoInsta360 ? 'rgba(0,212,255,0.08)' : 'rgba(240,247,255,0.04)',
          border: `1px solid ${state.addVideoInsta360 ? 'rgba(0,212,255,0.4)' : 'rgba(240,247,255,0.1)'}`,
          borderRadius: '14px', padding: '16px 20px', cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
      >
        <div style={{
          width: '44px', height: '44px', borderRadius: '12px',
          background: 'rgba(0,212,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name="VideoCameraIcon" size={22} style={{ color: '#00D4FF' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '15px' }}>
            {lang === 'it' ? 'Video Insta360' : 'Insta360 Video'}
          </div>
          <div style={{ fontSize: '13px', color: 'rgba(240,247,255,0.55)', marginTop: '2px' }}>
            {lang === 'it' ? 'Ricevi il video della tua sessione in alta qualità' : 'Receive your session video in high quality'}
          </div>
        </div>
        <div style={{ fontWeight: 700, fontSize: '16px', color: '#00D4FF', flexShrink: 0 }}>
          +€{VIDEO_INSTA360_PRICE}
        </div>
        <div style={{
          width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
          border: `2px solid ${state.addVideoInsta360 ? '#00D4FF' : 'rgba(240,247,255,0.3)'}`,
          background: state.addVideoInsta360 ? '#00D4FF' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {state.addVideoInsta360 && <Icon name="CheckIcon" size={12} style={{ color: '#0a1628' }} />}
        </div>
      </div>

      <div className="form-checkbox-row">
        <input
          type="checkbox"
          id="isGift"
          checked={state.isGift}
          onChange={(e) => update('isGift', e.target.checked)}
          className="form-checkbox"
        />
        <label htmlFor="isGift" style={{ fontSize: '14px', color: 'rgba(240,247,255,0.75)', lineHeight: 1.5, cursor: 'pointer' }}>
          {lang === 'it' ? '🎁 È un regalo — invieremo la conferma come gift voucher' : '🎁 This is a gift — we\'ll send the confirmation as a gift voucher'}
        </label>
      </div>

      <div>
        <label className="form-label">{lang === 'it' ? 'Note o richieste speciali' : 'Notes or special requests'}</label>
        <textarea
          value={state.notes}
          onChange={(e) => update('notes', e.target.value)}
          placeholder={lang === 'it' ? 'Es. allergie, esigenze particolari, livello esperienza...' : 'E.g. allergies, special needs, experience level...'}
          className="form-input"
          rows={3}
          style={{ resize: 'vertical', minHeight: '80px' }}
        />
      </div>
    </div>
  );

  // ─── STEP 3: DETAILS ─────────────────────────────────────────────────────
  const renderStep3 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <h3 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '22px', fontWeight: 700 }}>
        {lang === 'it' ? 'I tuoi dati' : 'Your details'}
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label className="form-label">{lang === 'it' ? 'Nome *' : 'First name *'}</label>
          <input
            type="text"
            value={state.firstName}
            onChange={(e) => update('firstName', e.target.value)}
            placeholder={lang === 'it' ? 'Marco' : 'Marco'}
            className={`form-input ${errors.firstName ? 'error' : ''}`}
            autoComplete="given-name"
          />
          {errors.firstName && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '4px' }}>{errors.firstName}</p>}
        </div>
        <div>
          <label className="form-label">{lang === 'it' ? 'Cognome *' : 'Last name *'}</label>
          <input
            type="text"
            value={state.lastName}
            onChange={(e) => update('lastName', e.target.value)}
            placeholder={lang === 'it' ? 'Ferretti' : 'Ferretti'}
            className={`form-input ${errors.lastName ? 'error' : ''}`}
            autoComplete="family-name"
          />
          {errors.lastName && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '4px' }}>{errors.lastName}</p>}
        </div>
      </div>

      <div>
        <label className="form-label">Email *</label>
        <input
          type="email"
          value={state.email}
          onChange={(e) => update('email', e.target.value)}
          placeholder="marco@example.com"
          className={`form-input ${errors.email ? 'error' : ''}`}
          autoComplete="email"
        />
        {errors.email && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '4px' }}>{errors.email}</p>}
      </div>

      <div>
        <label className="form-label">{lang === 'it' ? 'Telefono / WhatsApp *' : 'Phone / WhatsApp *'}</label>
        <input
          type="tel"
          value={state.phone}
          onChange={(e) => update('phone', e.target.value)}
          placeholder="+39 375 793 4676"
          className={`form-input ${errors.phone ? 'error' : ''}`}
          autoComplete="tel"
        />
        {errors.phone && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '4px' }}>{errors.phone}</p>}
      </div>

      <div>
        <label className="form-label">{lang === 'it' ? 'Lingua preferita' : 'Preferred language'}</label>
        <select
          value={state.preferredLang}
          onChange={(e) => update('preferredLang', e.target.value)}
          className={`form-select ${errors.preferredLang ? 'error' : ''}`}
        >
          <option value="">{lang === 'it' ? '— Seleziona —' : '— Select —'}</option>
          <option value="it">Italiano</option>
          <option value="en">English</option>
        </select>
        {errors.preferredLang && <p style={{ color: '#ff7070', fontSize: '12px', marginTop: '4px' }}>{errors.preferredLang}</p>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
        <div className="form-checkbox-row">
          <input
            type="checkbox"
            id="terms"
            checked={state.acceptTerms}
            onChange={(e) => update('acceptTerms', e.target.checked)}
            className="form-checkbox"
          />
          <label htmlFor="terms" style={{ fontSize: '13px', color: 'rgba(240,247,255,0.7)', lineHeight: 1.5, cursor: 'pointer' }}>
            {lang === 'it' ? 'Accetto i ' : 'I accept the '}
            <Link href="/terms" target="_blank" style={{ color: '#00D4FF' }}>
              {lang === 'it' ? 'Termini di servizio' : 'Terms of Service'}
            </Link>
            {' *'}
          </label>
        </div>
        {errors.acceptTerms && <p style={{ color: '#ff7070', fontSize: '12px', marginLeft: '32px' }}>{errors.acceptTerms}</p>}

        <div className="form-checkbox-row">
          <input
            type="checkbox"
            id="privacy"
            checked={state.acceptPrivacy}
            onChange={(e) => update('acceptPrivacy', e.target.checked)}
            className="form-checkbox"
          />
          <label htmlFor="privacy" style={{ fontSize: '13px', color: 'rgba(240,247,255,0.7)', lineHeight: 1.5, cursor: 'pointer' }}>
            {lang === 'it' ? 'Accetto la ' : 'I accept the '}
            <Link href="/privacy" target="_blank" style={{ color: '#00D4FF' }}>
              Privacy Policy
            </Link>
            {' *'}
          </label>
        </div>
        {errors.acceptPrivacy && <p style={{ color: '#ff7070', fontSize: '12px', marginLeft: '32px' }}>{errors.acceptPrivacy}</p>}

        <div className="form-checkbox-row">
          <input
            type="checkbox"
            id="reminder"
            checked={state.reminderWhatsapp}
            onChange={(e) => update('reminderWhatsapp', e.target.checked)}
            className="form-checkbox"
          />
          <label htmlFor="reminder" style={{ fontSize: '13px', color: 'rgba(240,247,255,0.55)', lineHeight: 1.5, cursor: 'pointer' }}>
            {lang === 'it' ? 'Accetto di ricevere promemoria via WhatsApp (T+2h e T-24h, ore 08–21)' : 'I agree to receive WhatsApp reminders (T+2h and T-24h, 08:00–21:00)'}
          </label>
        </div>
      </div>

      {bookingError && (
        <div className="alert-error" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon name="ExclamationCircleIcon" size={16} />
          {bookingError}
        </div>
      )}
    </div>
  );

  // ─── STEP 4 PAYMENT PHASE ────────────────────────────────────────────────
  const renderStep4Payment = () => {
    const pkgData = state.packageId ? PACKAGES[state.packageId as PackageId] : null;
    const locData = state.location ? LOCATIONS[state.location as LocationId] : null;
    const depositAllowed = pkgData?.depositAllowed ?? false;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

        <div>
          <h3 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '22px', fontWeight: 700, marginBottom: '6px' }}>
            {lang === 'it' ? 'Pagamento' : 'Payment'}
          </h3>
          <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.5)' }}>
            {lang === 'it' ? 'Slot riservato · Completa il pagamento per confermare' : 'Slot reserved · Complete payment to confirm'}
          </p>
        </div>

        <div style={{ background: '#0d2235', border: '1px solid rgba(240,247,255,0.08)', borderRadius: '14px', padding: '16px 20px' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(240,247,255,0.35)', marginBottom: '12px' }}>
            {lang === 'it' ? 'Riepilogo ordine' : 'Order summary'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(() => {
              const isTwoSessions = state.packageId === 'pkg_4h' && state.usageMode4h === 'two_sessions_two_days';
              const locationLabel = locData ? (lang === 'it' ? locData.nameIT : locData.nameEN) : '—';
              const baseRows = [
                { icon: 'TagIcon', label: lang === 'it' ? 'Pacchetto' : 'Package', value: pkgData ? (lang === 'it' ? pkgData.nameIT : pkgData.nameEN) : '—' },
                { icon: 'UserGroupIcon', label: lang === 'it' ? 'Partecipanti' : 'Participants', value: String(state.guests) },
              ];
              return (
                <>
                  {baseRows.map((row) => (
                    <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                      <Icon name={row.icon as 'TagIcon'} size={14} style={{ color: '#00D4FF', flexShrink: 0 }} />
                      <span style={{ color: 'rgba(240,247,255,0.5)', minWidth: '90px' }}>{row.label}</span>
                      <span style={{ fontWeight: 600 }}>{row.value}</span>
                    </div>
                  ))}
                  {isTwoSessions ? (
                    <>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', paddingLeft: '0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Icon name="CalendarDaysIcon" size={14} style={{ color: '#00D4FF', flexShrink: 0 }} />
                          <span style={{ color: 'rgba(240,247,255,0.5)', minWidth: '90px' }}>{lang === 'it' ? 'Sessione 1' : 'Session 1'}</span>
                          <span style={{ fontWeight: 600 }}>{state.session1Date} · {state.session1TimeSlot} · {locationLabel}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Icon name="CalendarDaysIcon" size={14} style={{ color: '#00D4FF', flexShrink: 0 }} />
                          <span style={{ color: 'rgba(240,247,255,0.5)', minWidth: '90px' }}>{lang === 'it' ? 'Sessione 2' : 'Session 2'}</span>
                          <span style={{ fontWeight: 600 }}>{state.session2Date} · {state.session2TimeSlot} · {locationLabel}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {[
                        { icon: 'MapPinIcon', label: lang === 'it' ? 'Location' : 'Location', value: locationLabel },
                        { icon: 'CalendarDaysIcon', label: lang === 'it' ? 'Data' : 'Date', value: state.date },
                        { icon: 'ClockIcon', label: lang === 'it' ? 'Orario' : 'Time', value: state.timeSlot },
                      ].map((row) => (
                        <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                          <Icon name={row.icon as 'TagIcon'} size={14} style={{ color: '#00D4FF', flexShrink: 0 }} />
                          <span style={{ color: 'rgba(240,247,255,0.5)', minWidth: '90px' }}>{row.label}</span>
                          <span style={{ fontWeight: 600 }}>{row.value}</span>
                        </div>
                      ))}
                    </>
                  )}
                </>
              );
            })()}
            {state.addVideoInsta360 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                <Icon name="VideoCameraIcon" size={14} style={{ color: '#00D4FF', flexShrink: 0 }} />
                <span style={{ color: 'rgba(240,247,255,0.5)', minWidth: '90px' }}>Extra</span>
                <span style={{ fontWeight: 600 }}>Video Insta360 +€{VIDEO_INSTA360_PRICE}</span>
              </div>
            )}
            {firstExpDiscount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                <Icon name="TagIcon" size={14} style={{ color: '#4ade80', flexShrink: 0 }} />
                <span style={{ color: 'rgba(240,247,255,0.5)', minWidth: '90px' }}>{lang === 'it' ? 'Sconto' : 'Discount'}</span>
                <span style={{ fontWeight: 600, color: '#4ade80' }}>{lang === 'it' ? 'Sconto 2 persone' : '2-person bundle'} −€{firstExpDiscount}</span>
              </div>
            )}
            {appliedDiscountCode && discountAmountCents > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                <Icon name="TagIcon" size={14} style={{ color: '#4ade80', flexShrink: 0 }} />
                <span style={{ color: 'rgba(240,247,255,0.5)', minWidth: '90px' }}>{lang === 'it' ? 'Codice sconto' : 'Discount code'}</span>
                <span style={{ fontWeight: 600, color: '#4ade80' }}>
                  {appliedDiscountCode.toUpperCase()} −€{(discountAmountCents / 100).toFixed(2)}
                </span>
              </div>
            )}
            <div style={{ height: '1px', background: 'rgba(240,247,255,0.08)', margin: '4px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px' }}>
              <span style={{ fontWeight: 700 }}>{lang === 'it' ? 'Totale sessione' : 'Session total'}</span>
              <span style={{ fontWeight: 700, color: '#00D4FF' }}>
                {appliedDiscountCode && discountAmountCents > 0 ? (
                  <>
                    <span style={{ textDecoration: 'line-through', color: 'rgba(240,247,255,0.35)', fontSize: '13px', marginRight: '6px' }}>
                      €{totalAmount}
                    </span>
                    €{((totalAmount * 100 - discountAmountCents) / 100).toFixed(2)}
                  </>
                ) : (
                  `€${totalAmount}`
                )}
              </span>
            </div>
          </div>
        </div>

        {/* ── DISCOUNT CODE INPUT ── */}
        <div style={{ background: '#0d2235', border: '1px solid rgba(240,247,255,0.08)', borderRadius: '14px', padding: '16px 20px' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(240,247,255,0.35)', marginBottom: '12px' }}>
            {lang === 'it' ? 'Hai un codice sconto?' : 'Have a discount code?'}
          </p>
          {appliedDiscountCode ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: '8px',
                background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)',
                borderRadius: '10px', padding: '10px 14px',
              }}>
                <Icon name="TagIcon" size={16} style={{ color: '#4ade80', flexShrink: 0 }} />
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#4ade80' }}>
                  {appliedDiscountCode.toUpperCase()}
                </span>
                <span style={{ fontSize: '13px', color: 'rgba(240,247,255,0.6)', marginLeft: 'auto' }}>
                  −€{(discountAmountCents / 100).toFixed(2)}
                </span>
              </div>
              <button
                onClick={handleRemoveDiscount}
                disabled={isApplyingDiscount}
                style={{
                  background: 'none', border: '1px solid rgba(255,80,80,0.3)', borderRadius: '10px',
                  color: '#ff7070', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                  padding: '10px 14px', whiteSpace: 'nowrap', opacity: isApplyingDiscount ? 0.6 : 1,
                }}
              >
                {lang === 'it' ? 'Rimuovi' : 'Remove'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                value={discountCode}
                onChange={(e) => { setDiscountCode(e.target.value); setDiscountError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyDiscount()}
                placeholder={lang === 'it' ? 'Inserisci il codice' : 'Enter code'}
                className="form-input"
                style={{ flex: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}
                disabled={isApplyingDiscount}
              />
              <button
                onClick={handleApplyDiscount}
                disabled={isApplyingDiscount || !discountCode.trim()}
                style={{
                  background: discountCode.trim() ? 'rgba(0,212,255,0.12)' : 'rgba(240,247,255,0.04)',
                  border: `1px solid ${discountCode.trim() ? 'rgba(0,212,255,0.4)' : 'rgba(240,247,255,0.1)'}`,
                  borderRadius: '10px', color: discountCode.trim() ? '#00D4FF' : 'rgba(240,247,255,0.3)',
                  cursor: discountCode.trim() && !isApplyingDiscount ? 'pointer' : 'not-allowed',
                  fontSize: '13px', fontWeight: 700, padding: '0 18px', whiteSpace: 'nowrap',
                  opacity: isApplyingDiscount ? 0.6 : 1, fontFamily: 'DM Sans, sans-serif',
                }}
              >
                {isApplyingDiscount ? (
                  <Spinner size={14} />
                ) : (
                  lang === 'it' ? 'Applica' : 'Apply'
                )}
              </button>
            </div>
          )}
          {discountError && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              marginTop: '8px', fontSize: '12px', color: '#ff7070',
            }}>
              <Icon name="ExclamationCircleIcon" size={14} />
              {discountError}
            </div>
          )}
        </div>

        {depositAllowed && (
          <div>
            <p style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(240,247,255,0.4)', marginBottom: '10px' }}>
              {lang === 'it' ? 'Modalità di pagamento' : 'Payment mode'}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {(['full', 'deposit'] as const).map((type) => {
                const isSelected = state.paymentType === type;
                const label = type === 'full'
                  ? (lang === 'it' ? 'Pagamento completo' : 'Full payment')
                  : (lang === 'it' ? 'Acconto 30%' : '30% Deposit');
                const sub = type === 'full'
                  ? `€${totalAmount}`
                  : `€${(totalAmount * 0.3).toFixed(2)} ${lang === 'it' ? 'ora' : 'now'}`;
                return (
                  <div
                    key={type}
                    onClick={() => handlePaymentTypeChange(type)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handlePaymentTypeChange(type)}
                    style={{
                      background: isSelected ? 'rgba(0,212,255,0.1)' : '#0d2235',
                      border: `1.5px solid ${isSelected ? '#00D4FF' : 'rgba(240,247,255,0.1)'}`,
                      borderRadius: '12px', padding: '14px', cursor: 'pointer', transition: 'all 0.2s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0,
                        border: `2px solid ${isSelected ? '#00D4FF' : 'rgba(240,247,255,0.2)'}`,
                        background: isSelected ? '#00D4FF' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {isSelected && <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#0B1F2E' }} />}
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 700 }}>{label}</span>
                    </div>
                    <p style={{ fontSize: '16px', fontWeight: 700, color: isSelected ? '#00D4FF' : 'rgba(240,247,255,0.6)', paddingLeft: '24px' }}>{sub}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isCreatingIntent && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '24px', color: 'rgba(240,247,255,0.5)', fontSize: '13px' }}>
            <Spinner size={20} />
            {lang === 'it' ? 'Preparazione pagamento...' : 'Preparing payment...'}
          </div>
        )}

        {paymentError && !isCreatingIntent && (
          <div style={{
            background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.3)',
            borderRadius: '10px', padding: '14px 16px',
            display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#ff7070',
          }}>
            <Icon name="ExclamationCircleIcon" size={16} />
            <span style={{ flex: 1 }}>{paymentError}</span>
            <button
              onClick={() => createPaymentIntent(state.reservationId, state.paymentType)}
              style={{ background: 'none', border: '1px solid rgba(255,80,80,0.4)', borderRadius: '8px', color: '#ff7070', cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: '6px 12px', whiteSpace: 'nowrap' }}
            >
              {lang === 'it' ? 'Riprova' : 'Retry'}
            </button>
          </div>
        )}

        {isConfirmingPayment && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '24px', color: 'rgba(240,247,255,0.5)', fontSize: '13px' }}>
            <Spinner size={20} />
            {lang === 'it' ? 'Conferma pagamento...' : 'Confirming payment...'}
          </div>
        )}

        {clientSecret && !isCreatingIntent && !isConfirmingPayment && (
          <Elements
            key={clientSecret}
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'night',
                variables: {
                  colorPrimary: '#00D4FF',
                  colorBackground: '#0d2235',
                  colorText: '#F0F7FF',
                  colorDanger: '#ff7070',
                  fontFamily: 'DM Sans, system-ui, sans-serif',
                  borderRadius: '10px',
                  spacingUnit: '4px',
                },
                rules: {
                  '.Input': { border: '1px solid rgba(240,247,255,0.12)', backgroundColor: '#122940' },
                  '.Input:focus': { border: '1px solid #00D4FF', boxShadow: '0 0 0 2px rgba(0,212,255,0.15)' },
                  '.Label': { color: 'rgba(240,247,255,0.6)', fontSize: '12px', fontWeight: '600', letterSpacing: '0.04em' },
                  '.Tab': { border: '1px solid rgba(240,247,255,0.1)', backgroundColor: '#122940' },
                  '.Tab--selected': { border: '1px solid #00D4FF', backgroundColor: 'rgba(0,212,255,0.08)' },
                },
              },
            }}
          >
            <StripePaymentFormInner
              amountCents={paymentAmountCents}
              totalAmountCents={paymentTotalCents}
              paymentType={state.paymentType}
              lang={lang}
              onSuccess={handlePaymentSuccess}
              onError={(msg) => setPaymentError(msg)}
            />
          </Elements>
        )}
      </div>
    );
  };

  // ─── STEP 4 TWO-SESSIONS PENDING PHASE ───────────────────────────────────
  const renderStep4TwoSessionsPending = () => {
    const locData = state.location ? LOCATIONS[state.location as LocationId] : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', paddingTop: '8px', textAlign: 'center' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: 'rgba(37,211,102,0.12)', border: '2px solid rgba(37,211,102,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="CalendarDaysIcon" size={28} style={{ color: '#25D366' }} />
          </div>
          <div>
            <h3 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '22px', fontWeight: 700, marginBottom: '6px' }}>
              {lang === 'it' ? 'Sessioni selezionate' : 'Sessions selected'}
            </h3>
            <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.5)' }}>
              {lang === 'it' ? `Ref: ${state.bookingRef}` : `Ref: ${state.bookingRef}`}
            </p>
          </div>
        </div>

        {/* Session summary */}
        <div style={{ background: '#0d2235', border: '1px solid rgba(240,247,255,0.08)', borderRadius: '14px', padding: '16px 20px' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(240,247,255,0.35)', marginBottom: '12px' }}>
            {lang === 'it' ? 'Riepilogo sessioni' : 'Sessions summary'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
              <Icon name="MapPinIcon" size={14} style={{ color: '#00D4FF', flexShrink: 0 }} />
              <span style={{ color: 'rgba(240,247,255,0.5)', minWidth: '90px' }}>{lang === 'it' ? 'Location' : 'Location'}</span>
              <span style={{ fontWeight: 600 }}>{locData ? (lang === 'it' ? locData.nameIT : locData.nameEN) : '—'}</span>
            </div>
            <div style={{ height: '1px', background: 'rgba(240,247,255,0.06)' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px' }}>
              <Icon name="CalendarDaysIcon" size={14} style={{ color: '#00D4FF', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: 'rgba(240,247,255,0.5)', marginBottom: '4px' }}>{lang === 'it' ? 'Sessione 1' : 'Session 1'}</div>
                <div style={{ fontWeight: 600 }}>{state.session1Date} · {state.session1TimeSlot}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px' }}>
              <Icon name="CalendarDaysIcon" size={14} style={{ color: '#00D4FF', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: 'rgba(240,247,255,0.5)', marginBottom: '4px' }}>{lang === 'it' ? 'Sessione 2' : 'Session 2'}</div>
                <div style={{ fontWeight: 600 }}>{state.session2Date} · {state.session2TimeSlot}</div>
              </div>
            </div>
            <div style={{ height: '1px', background: 'rgba(240,247,255,0.08)', margin: '2px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px' }}>
              <span style={{ fontWeight: 700 }}>{lang === 'it' ? 'Totale pacchetto' : 'Package total'}</span>
              <span style={{ fontWeight: 700, color: '#00D4FF' }}>€380</span>
            </div>
          </div>
        </div>

        {/* Safe pending message */}
        <div style={{
          background: 'rgba(37,211,102,0.07)',
          border: '1px solid rgba(37,211,102,0.25)',
          borderRadius: '14px',
          padding: '18px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="InformationCircleIcon" size={18} style={{ color: '#25D366', flexShrink: 0 }} />
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#25D366' }}>
              {lang === 'it' ? 'Prossimo passo' : 'Next step'}
            </span>
          </div>
          <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.75)', lineHeight: 1.65 }}>
            {lang === 'it' ?'Le due sessioni sono state selezionate. Il pagamento online per questa modalità sarà attivato a breve. Per completare ora la prenotazione, contattaci su WhatsApp.' :'The two sessions have been selected. Online payment for this mode will be enabled soon. To complete the booking now, contact us on WhatsApp.'}
          </p>
          <a
            href="https://wa.me/393757934676"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: '#25D366', color: '#fff', fontWeight: 700, fontSize: '14px',
              borderRadius: '10px', padding: '12px 20px', textDecoration: 'none',
              alignSelf: 'flex-start',
            }}
          >
            <Icon name="ChatBubbleLeftRightIcon" size={16} />
            {lang === 'it' ? 'Completa su WhatsApp' : 'Complete on WhatsApp'}
          </a>
        </div>
      </div>
    );
  };

  // ─── STEP 4 CONFIRMATION PHASE ───────────────────────────────────────────
  const renderStep4Confirmation = () => {
    const waiverUrl = state.waiverToken
      ? (lang === 'en' ? `/en/waiver?token=${state.waiverToken}` : `/it/firma?token=${state.waiverToken}`)
      : null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', textAlign: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', paddingTop: '16px' }}>
          <div style={{
            width: '72px', height: '72px', borderRadius: '50%',
            background: 'rgba(37,211,102,0.15)', border: '2px solid rgba(37,211,102,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'pulse-cyan 2s ease-in-out infinite',
          }}>
            <Icon name="CheckIcon" size={32} style={{ color: '#25D366' }} />
          </div>
          <div>
            <h2 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>
              {lang === 'it' ? 'Prenotazione confermata!' : 'Booking confirmed!'}
            </h2>
            <p style={{ color: 'rgba(240,247,255,0.6)', fontSize: '15px' }}>
              {lang === 'it'
                ? `Ref: ${state.bookingRef} · Conferma inviata a ${state.email}`
                : `Ref: ${state.bookingRef} · Confirmation sent to ${state.email}`}
            </p>
          </div>
        </div>

        <div className="alert-info" style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', textAlign: 'left' }}>
          <Icon name="ClockIcon" size={16} />
          <div style={{ fontSize: '13px' }}>
            <strong>{lang === 'it' ? 'Stato: PAGATO' : 'Status: PAID'}</strong><br />
            {lang === 'it' ? 'Pagamento ricevuto. Completa il check-in digitale per confermare definitivamente.' : 'Payment received. Complete digital check-in to fully confirm your booking.'}
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, rgba(0,212,255,0.12), rgba(0,212,255,0.06))',
          border: '1px solid rgba(0,212,255,0.3)',
          borderRadius: '20px',
          padding: '24px',
        }}>
          <p style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#00D4FF', marginBottom: '8px' }}>
            ⚡ {lang === 'it' ? 'Azione richiesta' : 'Action required'}
          </p>
          <h3 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>
            {lang === 'it' ? 'Completa il Check-In (2 min)' : 'Complete Check-In (2 min)'}
          </h3>
          <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.6)', marginBottom: '20px', lineHeight: 1.6 }}>
            {lang === 'it' ? 'Firma digitale rapida: requisiti fisici, attrezzatura e regole. Puoi farlo ora o entro 2h.' : 'Quick digital signature: physical requirements, equipment and rules. Do it now or within 2h.'}
          </p>

          {isLoadingWaiver ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '16px', color: 'rgba(240,247,255,0.5)', fontSize: '13px' }}>
              <Spinner size={18} />
              {lang === 'it' ? 'Generazione link waiver...' : 'Generating waiver link...'}
            </div>
          ) : waiverError ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
              <div className="alert-error" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', textAlign: 'left' }}>
                <Icon name="ExclamationCircleIcon" size={16} />
                {waiverError}
              </div>
              <button
                onClick={() => fetchWaiverToken(state.reservationId, state.firstName, state.lastName, state.email)}
                style={{ background: 'none', border: '1px solid rgba(0,212,255,0.4)', borderRadius: '8px', color: '#00D4FF', cursor: 'pointer', fontSize: '13px', fontWeight: 600, padding: '8px 16px' }}
              >
                {lang === 'it' ? 'Riprova' : 'Retry'}
              </button>
            </div>
          ) : waiverUrl ? (
            <Link
              href={waiverUrl}
              className="btn-primary"
              style={{ fontSize: '16px', padding: '16px 32px', display: 'inline-flex' }}
            >
              <Icon name="PencilSquareIcon" size={18} variant="solid" />
              {lang === 'it' ? 'COMPLETA IL CHECK-IN' : 'COMPLETE CHECK-IN'}
            </Link>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '16px', color: 'rgba(240,247,255,0.4)', fontSize: '13px' }}>
              <Spinner size={18} />
              {lang === 'it' ? 'Preparazione link...' : 'Preparing link...'}
            </div>
          )}

          <p style={{ fontSize: '11px', color: 'rgba(240,247,255,0.3)', marginTop: '10px' }}>
            {lang === 'it' ? 'Il link è personale e scade dopo 48h.' : 'This link is personal and expires after 48h.'}
          </p>
        </div>

        <div className="glass-cyan" style={{ borderRadius: '14px', padding: '16px 20px', textAlign: 'left' }}>
          <p style={{ fontSize: '13px', fontWeight: 700, color: '#00D4FF', marginBottom: '8px' }}>
            🎒 {lang === 'it' ? 'Cosa portare' : 'What to bring'}
          </p>
          <p style={{ fontSize: '13px', color: 'rgba(240,247,255,0.65)', lineHeight: 1.6 }}>
            {lang === 'it' ? 'Costume da bagno · Asciugamano · Crema solare · Acqua · Occhiali da sole' : 'Swimwear · Towel · Sunscreen · Water · Sunglasses'}
          </p>
        </div>

        <a
          href="https://wa.me/393757934676"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-whatsapp"
          style={{ justifyContent: 'center' }}
        >
          <Icon name="ChatBubbleLeftRightIcon" size={16} />
          {lang === 'it' ? 'Hai domande? Scrivici su WhatsApp' : 'Questions? Message us on WhatsApp'}
        </a>
      </div>
    );
  };

  // ─── NAVIGATION BUTTONS ──────────────────────────────────────────────────
  const renderNav = () => {
    if (step === 4) return null;

    const isStep3Submitting = step === 3 && isSubmitting;

    return (
      <div style={{ display: 'flex', gap: '12px', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid rgba(240,247,255,0.08)' }}>
        {step > 1 && (
          <button onClick={handleBack} disabled={isSubmitting} className="btn-secondary" style={{ flex: 1, opacity: isSubmitting ? 0.5 : 1 }}>
            <Icon name="ChevronLeftIcon" size={16} />
            {lang === 'it' ? 'Indietro' : 'Back'}
          </button>
        )}
        <button
          onClick={handleNext}
          disabled={isSubmitting}
          className="btn-primary"
          style={{ flex: step > 1 ? 2 : 1, justifyContent: 'center', opacity: isSubmitting ? 0.7 : 1 }}
        >
          {isStep3Submitting ? (
            <>
              <Spinner size={16} />
              {lang === 'it' ? 'Prenotazione in corso...' : 'Reserving slot...'}
            </>
          ) : (
            <>
              {lang === 'it' ? 'Continua' : 'Continue'}
              <Icon name="ChevronRightIcon" size={16} />
            </>
          )}
        </button>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: '24px 0 80px' }}>
      <StepIndicator step={step} lang={lang} />
      <div style={{
        background: '#122940', border: '1px solid rgba(240,247,255,0.08)', borderRadius: '20px',
        padding: 'clamp(20px, 5vw, 36px)',
        transition: 'opacity 0.3s ease',
      }}>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && step4Phase === 'payment' && renderStep4Payment()}
        {step === 4 && step4Phase === 'two_sessions_pending' && renderStep4TwoSessionsPending()}
        {step === 4 && step4Phase === 'confirmation' && renderStep4Confirmation()}
        {renderNav()}
      </div>
    </div>
  );
}

export default function BookingEngine({ lang = 'it' }: { lang?: 'it' | 'en' }) {
  return (
    <Suspense fallback={
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px', color: 'rgba(240,247,255,0.4)', fontSize: '14px' }}>
        {lang === 'it' ? 'Caricamento...' : 'Loading...'}
      </div>
    }>
      <BookingEngineInner lang={lang} />
    </Suspense>
  );
}
